/**
 * Hotel Update Price Thunk
 *
 * Updates price with PAN/TCS details
 * POST /hotels/update-price
 *
 * Called when:
 * 1. User enters PAN and TCS collection is enabled
 * 2. TCS toggle changes
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchHotelUpdatePrice as fetchHotelUpdatePriceApi,
  HotelUpdatePriceError,
  type UpdatePriceSummaryData,
  type UpdatePriceRequestBody,
} from '../../api';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface UpdatePriceResult {
  priceSummary: UpdatePriceSummaryData | null;
  statusMessage: string | null;
  ratePlanList: unknown[];
}

// ============================================
// Thunk
// ============================================

/**
 * Update price with PAN/TCS details
 * POST /hotels/update-price
 */
export const updatePriceThunk = createAsyncThunk<
  UpdatePriceResult,
  UpdatePriceRequestBody,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'hotels/updatePrice',
  async (body, { rejectWithValue }) => {
    try {
      const sessionId = getOrCreateSessionId();
      const response = await fetchHotelUpdatePriceApi({
        sessionId,
        userId: getUUID(),
        body,
      });

      // Handle API error response
      if (!response.success) {
        const errorMessage = response.error?.message ?? 'Update price failed';
        logger.error('[UpdatePriceThunk] Update price failed:', response.error);
        return rejectWithValue(errorMessage);
      }

      return {
        priceSummary: response.priceSummary,
        statusMessage: response.statusMessage,
        ratePlanList: response.ratePlanList,
      };
    } catch (error) {
      logger.error('[UpdatePriceThunk] Failed to update price:', error);
      if (error instanceof HotelUpdatePriceError) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Failed to update price');
    }
  },
  {
    // Prevent duplicate API calls
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      const { isUpdatePriceLoading } = state.hotels.bookingReview;
      return !isUpdatePriceLoading;
    },
  },
);
